import { getAllHeaders, getDefaultConfig } from '.';
import type { LayerConfig } from './types';
import { LayerType } from './types';
import { MVTLayer } from '@deck.gl/geo-layers';
import type { MVTLayerProps } from '@deck.gl/geo-layers';
import { GeoJsonLayer } from '@deck.gl/layers';
import type { Color } from '@deck.gl/core';
import type { FilterState } from '@/features/filterSlice';
import { generateBase64FilterQuery } from '@/features/filterHelpers';
import {
  getAnalysisColor,
  getAnalysisColors,
  getWildfireRiskFillColor,
} from '@/app/utils';
import { analysisView } from '@/app/analysis';
import {
  GroupAnalysisType,
  type GroupSummary,
} from '@/generated/types/navigator/types.gen';
import { addMvtCachingParams } from '@/features/utils';
import type { AnalysisFeature, GroupId } from '@/features/types';

export const isSpan = (
  d: AnalysisFeature,
  spanNetworkGroupId: GroupId | null,
  activeGroupName: string | null
): boolean =>
  d.properties.networkGroupId === spanNetworkGroupId ||
  activeGroupName !== null;

export const isWildfireRiskGroup = (
  d: AnalysisFeature,
  selectedAnalysis: GroupAnalysisType,
  hasGroupRiskSummary: boolean,
  spanNetworkGroupId: GroupId | null,
  activeGroupName: string | null
): boolean =>
  selectedAnalysis === GroupAnalysisType.WILDFIRE_PRIORITY &&
  hasGroupRiskSummary &&
  !isSpan(d, spanNetworkGroupId, activeGroupName);

// A white halo behind each line lifts the network off the basemap. Sub-pixel spans in the
// polygon view need no lifting and a 10px stroke around each footprint would white out the
// basemap between neighbours, so it is drawn for line geometry only — tiles below
// SPAN_POLYGON_ZOOM, matching what the MVT endpoint emits.
const HALO_WIDTH = 10;
const HALO_COLOR: Color = [255, 255, 255, 128];
const SPAN_POLYGON_ZOOM = 15;

type TileSubLayerProps = Parameters<
  NonNullable<MVTLayerProps['renderSubLayers']>
>[0];

const withHalo = (props: TileSubLayerProps): GeoJsonLayer[] => {
  const halo = new GeoJsonLayer({
    ...props,
    id: `${props.id}-halo`,
    pickable: false,
    autoHighlight: false,
    highlightedObjectIndex: -1,
    filled: false,
    stroked: true,
    getLineColor: HALO_COLOR,
    // Override every inherited width prop, or the parent's scale/cap shrink the halo.
    getLineWidth: HALO_WIDTH,
    lineWidthScale: 1,
    lineWidthUnits: 'pixels',
    lineWidthMinPixels: HALO_WIDTH,
    lineWidthMaxPixels: HALO_WIDTH,
  });

  return [halo, new GeoJsonLayer(props)];
};

// In span view the map colors features from MVT tile properties, so the group
// summary is never read. Skipping the request avoids fetching a per-span
// aggregation that can run to tens of thousands of rows.
export const shouldSkipGroupSummary = (
  selectedNetworkGroupId: GroupId | null | undefined,
  spanNetworkGroupId: GroupId | null | undefined,
  activeGroupName: string | null
): boolean =>
  selectedNetworkGroupId == null ||
  selectedNetworkGroupId === spanNetworkGroupId ||
  activeGroupName !== null;

export const getLoadOptions = (user: string, usePost: boolean) => ({
  fetch: {
    method: usePost ? 'POST' : 'GET',
    headers: getAllHeaders(
      { 'Content-Type': 'application/json', 'Content-Encoding': 'gzip' },
      user
    ),
    credentials: 'include',
  },
});

export const getNetworkGroupLayer = (
  isOverview = true,
  config: LayerConfig,
  user: string,
  query: FilterState,
  selectedAnalysis: GroupAnalysisType,
  listId: string | null,
  selectedNetworkGroupId: GroupId | null,
  encroachmentThresholds: number[] = [],
  priorityThresholds: number[] = [],
  wildfirePriorityThresholds: number[] = [],
  criticalTreesThresholds: number[] = [],
  clearThresholds: number[] = [],
  spanNetworkGroupId: GroupId | null = null,
  activeGroupName: string | null = null,
  zoom: number,
  groupSummary: Map<string, GroupSummary>,
  statusChanged: boolean = false,
  mapNetworkGroupId: GroupId | null,
  mvtCacheCounter: number,
  hoveredGroupName: string | null = null,
  hasGroupRiskSummary = false,
  usePostForMvtGroupAnalysis = false,
  showWorkStatus = true,
  scopeQuery?: string,
  planId?: string,
  minZoom = 5,
  maxZoom = 24
) => {
  const loadOptions = getLoadOptions(user, usePostForMvtGroupAnalysis);

  const id = LayerType[LayerType.scan];

  let url = `/v1/analysis/group/{z}/{x}/{y}`;
  const useLegacyFilters = scopeQuery === undefined && planId === undefined;

  // Include the filter in the URL so tiles refresh when it changes.
  const params: Record<string, string | number | boolean | null | undefined> = {
    analysis_type: selectedAnalysis,
    group_id: String(mapNetworkGroupId),
    reload: statusChanged,
    mvt_cache_counter: mvtCacheCounter,
    ...(useLegacyFilters
      ? { filter_query: generateBase64FilterQuery(query), list_ids: listId }
      : { scope_query: scopeQuery, plan_id: planId }),
  };

  // parameters related to active group
  if (useLegacyFilters && activeGroupName && selectedNetworkGroupId) {
    params.active_group_id = selectedNetworkGroupId;
    params.active_group_name = activeGroupName;
  }

  // Adding parameters for mvt caching
  url = `${addMvtCachingParams(params, url)}`;

  const spanAnalysisColors = getAnalysisColors(
    spanNetworkGroupId,
    spanNetworkGroupId,
    encroachmentThresholds,
    priorityThresholds,
    wildfirePriorityThresholds,
    criticalTreesThresholds,
    clearThresholds,
    activeGroupName
  );
  const groupAnalysisColors = getAnalysisColors(
    null,
    spanNetworkGroupId,
    encroachmentThresholds,
    priorityThresholds,
    wildfirePriorityThresholds,
    criticalTreesThresholds,
    clearThresholds,
    null
  );

  const getDefaultFillColor = (d: AnalysisFeature): Color =>
    analysisView[selectedAnalysis].colorConfig?.fillColor(
      d,
      isOverview
    ) as Color;

  // The work-status overlay recolors spans by their worklog status. When the
  // overlay is toggled off, hide it by coloring as if the span had no status.
  const forColoring = (d: AnalysisFeature): AnalysisFeature =>
    !showWorkStatus && d.properties.workStatus
      ? { ...d, properties: { ...d.properties, workStatus: undefined } }
      : d;

  const props = {
    ...getDefaultConfig(id, user),
    id,
    ...config,
    loadOptions,
    data: selectedNetworkGroupId !== null ? url : null,
    minZoom,
    maxZoom,
    pickable: true,
    lineWidthScale: 15,
    getFillColor: (d: AnalysisFeature): Color => {
      const feature = forColoring(d);
      return isWildfireRiskGroup(
        feature,
        selectedAnalysis,
        hasGroupRiskSummary,
        spanNetworkGroupId,
        activeGroupName
      )
        ? getWildfireRiskFillColor(
            feature,
            groupAnalysisColors,
            groupSummary,
            isOverview
          )
        : getDefaultFillColor(feature);
    },
    getLineWidth: 1,
    getLineColor: (d: AnalysisFeature) => {
      if (hoveredGroupName && d.properties.group === hoveredGroupName) {
        return [0, 204, 207, 255] as Color;
      }

      const useSpanColors = isSpan(d, spanNetworkGroupId, activeGroupName);
      return getAnalysisColor(
        d,
        useSpanColors ? spanAnalysisColors : groupAnalysisColors,
        selectedAnalysis,
        groupSummary,
        useSpanColors,
        hasGroupRiskSummary
      ) as Color;
    },
    updateTriggers: {
      getFillColor: [groupSummary, hasGroupRiskSummary, showWorkStatus],
      getLineColor: [groupSummary, hoveredGroupName, hasGroupRiskSummary],
    },
    lineWidthMaxPixels: 2,
    highlightColor: isOverview ? [0, 0, 0, 100] : [255, 255, 255, 10],
    lineJointRounded: true,
    filled: true,
    stroked: true,
    renderSubLayers: (props: TileSubLayerProps) => {
      const render =
        props.tile.zoom < SPAN_POLYGON_ZOOM
          ? withHalo
          : (p: TileSubLayerProps) => new GeoJsonLayer(p);

      if (
        selectedNetworkGroupId === spanNetworkGroupId ||
        activeGroupName !== null
      ) {
        return render(props);
      }
      // Span geometry and risk colors are always displayed from zoom 14.5 (rounded to 15 in DeckGL) and higher,
      // regardless of the Network "Group By" selection. To prevent lingering tiles from the previous
      // zoom level during transitions between group and span views, we exclude them from rendering
      // if they exist in the tile cache.
      const { tile } = props;
      if (
        (tile.zoom === 14 && Math.round(zoom) === 15) ||
        (tile.zoom === 15 && Math.round(zoom) === 14)
      ) {
        return null;
      }
      return render(props);
    },
  };

  return new MVTLayer({ ...props });
};
